import React from 'react';

// ─── Button ────────────────────────────────────────────────────────────────
export function Button({ children, variant = 'ghost', size = 'md', onClick, disabled, type = 'button', fullWidth, style }) {
  const base = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    border: '1px solid var(--border-mid)',
    borderRadius: 'var(--radius-md)',
    background: 'var(--surface)',
    color: 'var(--text-soft)',
    fontFamily: 'var(--font-sans)',
    fontWeight: 400,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    transition: 'all 0.15s',
    width: fullWidth ? '100%' : undefined,
    ...style,
  };

  const sizes = {
    sm: { fontSize: '12px', padding: '5px 12px' },
    md: { fontSize: '13px', padding: '8px 16px' },
    lg: { fontSize: '14px', padding: '11px 22px' },
  };

  const variants = {
    ghost: {},
    primary: { background: 'var(--ocean)', borderColor: 'var(--ocean)', color: '#fff' },
    sand: { background: 'var(--sand)', borderColor: 'var(--sand)', color: 'var(--navy)', fontWeight: 500 },
    danger: { background: 'rgba(231,76,60,0.15)', borderColor: 'rgba(231,76,60,0.4)', color: '#f87171' },
  };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      style={{ ...base, ...sizes[size], ...variants[variant] }}
    >
      {children}
    </button>
  );
}

// ─── Badge ─────────────────────────────────────────────────────────────────
export function Badge({ status }) {
  const map = {
    confirmed: { bg: 'var(--status-confirmed)', color: 'var(--status-confirmed-text)', label: 'Confermata' },
    pending: { bg: 'var(--status-pending)', color: 'var(--status-pending-text)', label: 'In attesa' },
    cancelled: { bg: 'var(--status-cancelled)', color: 'var(--status-cancelled-text)', label: 'Cancellata' },
  };
  const s = map[status] || map.pending;
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 9px',
      borderRadius: '20px',
      fontSize: '11px',
      fontWeight: 500,
      background: s.bg,
      color: s.color,
    }}>
      {s.label}
    </span>
  );
}

// ─── Input ─────────────────────────────────────────────────────────────────
export function Input({ label, error, ...props }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      {label && (
        <label style={{ fontSize: '12px', color: 'var(--text-muted)', letterSpacing: '0.05em' }}>
          {label}
        </label>
      )}
      <input
        style={{
          background: 'var(--surface)',
          border: `1px solid ${error ? 'rgba(231,76,60,0.5)' : 'var(--border-mid)'}`,
          borderRadius: 'var(--radius-md)',
          color: 'var(--text-primary)',
          padding: '9px 13px',
          fontSize: '13.5px',
          outline: 'none',
          transition: 'border-color 0.15s',
          width: '100%',
        }}
        {...props}
      />
      {error && <span style={{ fontSize: '11.5px', color: '#f87171' }}>{error}</span>}
    </div>
  );
}

// ─── Spinner ───────────────────────────────────────────────────────────────
export function Spinner({ size = 24 }) {
  return (
    <svg
      width={size} height={size} viewBox="0 0 24 24" fill="none"
      style={{ animation: 'spin 0.8s linear infinite' }}
    >
      <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
      <circle cx="12" cy="12" r="9" stroke="var(--border-mid)" strokeWidth="2.5" />
      <path d="M12 3 A9 9 0 0 1 21 12" stroke="var(--ocean)" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}

// ─── Panel ─────────────────────────────────────────────────────────────────
export function Panel({ children, style }) {
  return (
    <div style={{
      background: 'var(--navy-mid)',
      border: '1px solid var(--border)',
      borderRadius: 'var(--radius-lg)',
      ...style,
    }}>
      {children}
    </div>
  );
}

export function PanelHead({ title, subtitle, action }) {
  return (
    <div style={{
      padding: '16px 20px 12px',
      borderBottom: '1px solid var(--border)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    }}>
      <div>
        <div style={{ fontSize: '14px', fontWeight: 500 }}>{title}</div>
        {subtitle && <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>{subtitle}</div>}
      </div>
      {action}
    </div>
  );
}

export function PanelBody({ children, style }) {
  return <div style={{ padding: '16px 20px', ...style }}>{children}</div>;
}

// ─── StatCard ──────────────────────────────────────────────────────────────
export function StatCard({ label, value, sub, delta, deltaUp, accentColor = 'var(--ocean)' }) {
  return (
    <div style={{
      background: 'var(--navy-mid)',
      border: '1px solid var(--border)',
      borderRadius: 'var(--radius-lg)',
      padding: '16px 18px',
      position: 'relative',
      overflow: 'hidden',
    }}>
      <div style={{
        position: 'absolute', top: 0, right: 0,
        width: '60px', height: '60px',
        borderRadius: '0 var(--radius-lg) 0 60px',
        background: accentColor, opacity: 0.12,
      }} />
      <div style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '6px' }}>
        {label}
      </div>
      <div style={{ fontSize: '28px', fontWeight: 300, lineHeight: 1 }}>
        {value}
        {sub && <span style={{ fontSize: '13px', color: 'var(--text-muted)', fontWeight: 400, marginLeft: '4px' }}>{sub}</span>}
      </div>
      {delta && (
        <div style={{ fontSize: '11.5px', marginTop: '6px', color: deltaUp ? '#4ade80' : '#f87171' }}>
          {deltaUp ? '↑' : '↓'} {delta}
        </div>
      )}
    </div>
  );
}

// ─── EmptyState ────────────────────────────────────────────────────────────
export function EmptyState({ message = 'Nessun dato disponibile' }) {
  return (
    <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-muted)' }}>
      <div style={{ fontSize: '28px', marginBottom: '10px' }}>🏖️</div>
      <div style={{ fontSize: '13px' }}>{message}</div>
    </div>
  );
}
